// MODIFIED: the About section used to be a bordered white card on its own full-height grey
// slab, which read as a separate page stapled under the hero. It is now laid out as the
// continuation of the landing page: a divider out of the hero, a numbered "how it works"
// row, the three feature cards, and one closing call to action.
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../Components/AuthProvider';

export default function About() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth(); // ADDED: signed-out visitors go to /login first

    // ADDED: the three steps, so a first-time visitor knows what the app actually asks of them
    const steps = [
        { number: '01', title: 'Add the model', body: 'Photograph the front and back of the package and record its details once.' },
        { number: '02', title: 'File it away', body: 'Drop it into as many of your collections as it belongs to.' },
        { number: '03', title: 'Watch it grow', body: 'Each collection keeps its own running total as you add cars.' },
    ];

    const features = [
        {
            title: 'Catalog every model',
            body: 'Record the series code, make, model, colour, scale, year and package series for each car, with photos of the front and back of the package.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h12A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118.25 20.25H6A2.25 2.25 0 013.75 18V6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9h18M9 21V9" />
                </svg>
            ),
        },
        {
            title: 'Organise into collections',
            body: 'Group your models however you like, by series, by make, or by whatever makes sense to you. A model can sit in more than one collection.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
                </svg>
            ),
        },
        {
            title: 'Track what it is worth',
            body: 'Each collection keeps a running total of the market value of the models inside it, updated as you add and remove cars.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
        },
    ];

    // Protected routes bounce signed-out visitors to /login anyway - sending them
    // there directly avoids a redirect flash.
    const go = (path) => navigate(isAuthenticated ? path : '/login');

    return (
        <>
            {/* Checkered band: the seam between the hero and the rest of the page */}
            <div className="cv-checkers" />

            <section id="about" className="py-16 sm:py-20 px-4 sm:px-8"
                     style={{ backgroundColor: 'var(--surface-raised)' }}>
                <div className="max-w-5xl mx-auto space-y-16">

                    {/* Intro */}
                    <div className="text-center max-w-2xl mx-auto space-y-4">
                        <p className="cv-eyebrow">About</p>
                        <h2 className="cv-display text-3xl sm:text-4xl">A shared garage</h2>
                        <p className="cv-body text-base sm:text-lg leading-relaxed">
                            SpeedVault keeps a catalog of Hot Wheels models and lets you record which of
                            them you own. The catalog is shared, so once a model has been added, anyone can
                            drop it straight into their own collection without typing it all out again.
                        </p>
                    </div>

                    {/* How it works: three numbered steps, stacked on phones */}
                    <div className="grid gap-6 sm:grid-cols-3">
                        {steps.map((step) => (
                            <div key={step.number} className="text-center sm:text-left">
                                {/* The oversized number is the personality here - pit-board styling */}
                                <span className="font-display font-bold text-5xl text-brand-flame/25 leading-none">
                                    {step.number}
                                </span>
                                <h3 className="cv-title text-lg mt-2">{step.title}</h3>
                                <p className="text-sm cv-muted mt-1 leading-relaxed">{step.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* Feature cards */}
                    <div className="grid gap-5 sm:grid-cols-3">
                        {features.map((feature) => (
                            <div key={feature.title} className="cv-card cv-speedline p-5 sm:p-6 text-left">
                                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-flame/10 text-brand-flame">
                                    {feature.icon}
                                </span>
                                <h3 className="cv-title text-lg mt-4">{feature.title}</h3>
                                <p className="text-sm cv-muted mt-2 leading-relaxed">{feature.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* Closing call to action, on a solid red band so the page ends on the brand */}
                    <div className="rounded-2xl bg-brand-flame text-white overflow-hidden">
                        <div className="p-8 sm:p-10 text-center space-y-5">
                            <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl text-white">
                                Ready to open the vault?
                            </h2>
                            <p className="text-sm sm:text-base text-white/85 max-w-md mx-auto">
                                Browse what is already in the catalog, or start filing your own cars away.
                            </p>
                            {/* MODIFIED: stacks on phones, inline from sm up */}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                                <button onClick={() => go('/models')}
                                    className="cv-btn bg-white text-brand-flame hover:bg-brand-mist uppercase px-7">
                                    Browse the catalog
                                </button>
                                <button onClick={() => go('/collections')}
                                    className="cv-btn border-white/70 text-white hover:bg-white/15 uppercase px-7">
                                    My collections
                                </button>
                            </div>
                        </div>
                        <div className="cv-checkers opacity-25" />
                    </div>
                </div>
            </section>
        </>
    );
}
